import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProShell } from "@/components/ProShell";
import { StatusBadge } from "@/components/StatusBadge";
import { ProApplication } from "@/pages/pro/ProApplication";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { CaregiverVisits } from "@/components/CaregiverVisits";
import { HcNurseVisits } from "@/components/HcNurseVisits";
import { Loader2, ChevronRight, MapPin, Briefcase, Star, Power } from "lucide-react";

const ACTIVE = ["PROFESSIONAL ASSIGNED", "CONFIRMED", "ON THE WAY", "ARRIVED", "INSPECTION", "ESTIMATE APPROVAL REQUIRED", "WORK IN PROGRESS", "CUSTOMER REVIEW", "REVISION REQUIRED", "WORK DONE - PAYMENT PENDING"];

const OFFLINE_OPTS = [
    { key: "30m", label: "30 min" }, { key: "1h", label: "1 hour" }, { key: "2h", label: "2 hours" },
    { key: "4h", label: "4 hours" }, { key: "half_day", label: "Half day" },
];

function AvailabilityCard() {
    const [av, setAv] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showOpts, setShowOpts] = useState(false);

    const load = () => api.get("/pro/availability").then((r) => setAv(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const goOnline = async () => {
        setBusy(true);
        try { const { data } = await api.post("/pro/availability", { status: "online" }); setAv(data); setShowOpts(false); toast.success("You're online — eligible for new jobs"); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
    };
    const goOffline = async (duration) => {
        setBusy(true);
        try { const { data } = await api.post("/pro/availability", { status: "offline", duration }); setAv(data); setShowOpts(false); toast.success("You're offline — no new jobs will be assigned"); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
    };

    const online = av?.status === "online";
    return (
        <div className="mb-5 rounded-2xl border border-navy/10 bg-white p-4" data-testid="availability-card">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <span className={`h-2.5 w-2.5 rounded-full ${online ? "bg-emerald-500" : "bg-navy/30"}`} />
                    <div>
                        <h3 className="font-heading font-bold text-navy" data-testid="availability-status">{online ? "Online" : "Offline"}</h3>
                        <p className="text-xs text-navy/50">{online ? "You can receive new assignments" : av?.offline_until ? `Back online at ${new Date(av.offline_until).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "No new assignments"}</p>
                    </div>
                </div>
                <button data-testid="availability-toggle-btn" disabled={busy} onClick={() => (online ? setShowOpts((s) => !s) : goOnline())} className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white transition-transform active:scale-95 disabled:opacity-60 ${online ? "bg-navy/80" : "bg-emerald-500"}`}>
                    <Power size={14} /> {online ? "GO OFFLINE" : "GO ONLINE NOW"}
                </button>
            </div>
            {showOpts && online && (
                <div className="mt-3 border-t border-navy/10 pt-3">
                    <p className="mb-2 text-xs font-semibold text-navy/60">Go offline for…</p>
                    <div className="flex flex-wrap gap-2">
                        {OFFLINE_OPTS.map((o) => (
                            <button key={o.key} data-testid={`offline-${o.key}`} disabled={busy} onClick={() => goOffline(o.key)} className="rounded-full border border-navy/15 px-3 py-1.5 text-xs font-semibold text-navy transition-colors hover:border-orange disabled:opacity-60">{o.label}</button>
                        ))}
                    </div>
                    <p className="mt-2 text-[11px] text-navy/40">You'll automatically go back online when the time is up.</p>
                </div>
            )}
        </div>
    );
}

export default function ProJobs() {
    const [jobs, setJobs] = useState(null);
    const { user } = useAuth();
    const nav = useNavigate();

    const pro = user?.professional;
    const approved = ["Approved", "Verified"].includes(pro?.verification_status);

    useEffect(() => { if (approved) api.get("/bookings").then((r) => setJobs(r.data)).catch(() => setJobs([])); }, [approved]);

    if (user && pro && !approved) return <ProApplication embedded={false} />;

    const active = jobs?.filter((j) => ACTIVE.includes(j.status)) || [];
    const past = jobs?.filter((j) => !ACTIVE.includes(j.status)) || [];

    return (
        <ProShell>
            <div className="rounded-b-3xl bg-navy px-5 pb-6 pt-5 text-white">
                <div className="flex items-center justify-between">
                    <Logo size={34} light />
                    <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold">{pro?.pro_id}</span>
                </div>
                <h1 className="mt-4 font-heading text-2xl font-extrabold">Hi, {user?.name?.split(" ")[0]}</h1>
                <div className="mt-1 flex items-center gap-3 text-sm text-white/70">
                    <span className="flex items-center gap-1"><Star size={13} className="fill-orange text-orange" /> {pro?.rating || "New"}</span>
                    <span>·</span>
                    <span>{pro?.verification_status}</span>
                </div>
            </div>

            <div className="px-5 py-5">
                <AvailabilityCard />
                {pro?.track === "care" && <CaregiverVisits />}
                <HcNurseVisits />
                {jobs === null && <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}

                <h2 className="mb-3 font-heading text-lg font-bold text-navy">Active jobs ({active.length})</h2>
                {jobs && active.length === 0 && (
                    <div className="mb-6 flex flex-col items-center rounded-2xl border border-dashed border-navy/15 py-10 text-center">
                        <Briefcase size={34} className="text-navy/25" />
                        <p className="mt-2 text-sm text-navy/50">No active jobs right now.</p>
                    </div>
                )}
                <div className="space-y-3">{active.map((j) => <JobCard key={j.id} j={j} onClick={() => nav(`/pro/jobs/${j.id}`)} />)}</div>

                {past.length > 0 && (
                    <>
                        <h2 className="mb-3 mt-7 font-heading text-lg font-bold text-navy">Completed ({past.length})</h2>
                        <div className="space-y-3">{past.map((j) => <JobCard key={j.id} j={j} onClick={() => nav(`/pro/jobs/${j.id}`)} />)}</div>
                    </>
                )}
            </div>
        </ProShell>
    );
}

function JobCard({ j, onClick }) {
    return (
        <button data-testid={`pro-job-${j.booking_id}`} onClick={onClick} className="flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left transition-transform active:scale-[0.98]">
            <div className="flex-1">
                <span className="font-heading text-sm font-extrabold text-navy">{j.booking_id}</span>
                <p className="mt-0.5 text-sm font-medium text-navy/80">{j.service_name} · {j.problem}</p>
                <p className="flex items-center gap-1 text-xs text-navy/45"><MapPin size={12} /> {j.area_name} · {j.preferred_date}</p>
                <div className="mt-2"><StatusBadge status={j.status} /></div>
            </div>
            <ChevronRight size={20} className="text-navy/30" />
        </button>
    );
}
